import React, { useState } from 'react';
import type { Courrier, AppData } from '../types';
import { Button, Card, Input, Select } from './UI';

interface CourriersListProps {
  data: AppData;
  onDataChange: (data: AppData) => void;
}

const COURRIER_TEMPLATES: Record<string, string> = {
  'Mise en demeure': `Monsieur, Madame,

Par la présente, nous vous mettons en demeure de [ACTION REQUISE] dans un délai de [DÉLAI] jours à compter de la réception de ce courrier.

[DÉTAILS]

À défaut, nous nous verrons contraints de [RECOURS].

Cordialement,
[AVOCAT]`,

  'Lettre simple': `Monsieur, Madame,

[CONTENU]

Cordialement,
[AVOCAT]`,

  'Email': `[CONTENU]`,

  'Mémoire': `À L'ATTENTION DE LA JURIDICTION

[PARTIES]

MÉMOIRE

[CONTENU]`,

  'Assignation': `ASSIGNATION

[PARTIES]

[CONTENU]`,
};

export function CourriersList({ data, onDataChange }: CourriersListProps) {
  const [isCreating, setIsCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<Partial<Courrier>>({});

  const dossiers = data.dossiers;

  const handleCreate = () => {
    setIsCreating(true);
    setFormData({
      id: `c${Date.now()}`,
      dossier: dossiers[0]?.id || '',
      titre: '',
      contenu: '',
      date: new Date().toISOString().split('T')[0],
      type: 'Lettre',
      statut: 'Brouillon',
      destinataire: '',
    });
  };

  const handleEdit = (courrier: Courrier) => {
    setEditingId(courrier.id);
    setFormData(courrier);
  };

  const handleApplyTemplate = (templateType: string) => {
    const template = COURRIER_TEMPLATES[templateType];
    if (template) {
      setFormData({ ...formData, contenu: template });
    }
  };

  const handleSave = () => {
    if (!formData.dossier || !formData.titre) {
      alert('Veuillez remplir les champs requis');
      return;
    }

    const newData = { ...data };
    const dossier = newData.dossiers.find((d) => d.id === formData.dossier);

    if (isCreating) {
      newData.courriers.push(formData as Courrier);
      newData.journal.push({
        id: `j${Date.now()}`,
        dossier: formData.dossier!,
        action: 'Courrier créé',
        detail: `${formData.type} — ${formData.titre}`,
        ts: new Date().toISOString(),
      });
    } else {
      newData.courriers = newData.courriers.map((c) =>
        c.id === editingId ? (formData as Courrier) : c
      );
      newData.journal.push({
        id: `j${Date.now()}`,
        dossier: formData.dossier!,
        action: 'Courrier modifié',
        detail: `${formData.type} — ${formData.titre}`,
        ts: new Date().toISOString(),
      });
    }

    onDataChange(newData);
    setIsCreating(false);
    setEditingId(null);
    setFormData({});
  };

  const handleDelete = (id: string) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer ce courrier ?')) return;

    const courrier = data.courriers.find((c) => c.id === id);
    const newData = { ...data };
    newData.courriers = newData.courriers.filter((c) => c.id !== id);
    newData.journal.push({
      id: `j${Date.now()}`,
      dossier: courrier?.dossier || '',
      action: 'Courrier supprimé',
      detail: `${courrier?.type} — ${courrier?.titre}`,
      ts: new Date().toISOString(),
    });

    onDataChange(newData);
  };

  const handleCancel = () => {
    setIsCreating(false);
    setEditingId(null);
    setFormData({});
  };

  if (isCreating || editingId) {
    const dossier = dossiers.find((d) => d.id === formData.dossier);

    return (
      <div>
        <h1 style={{ margin: '0 0 1.5rem', fontSize: 28 }}>
          {isCreating ? 'Créer un courrier' : 'Éditer le courrier'}
        </h1>

        <Card style={{ maxWidth: 900 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: '0.25rem' }}>
                  Dossier *
                </label>
                <Select
                  value={formData.dossier || ''}
                  onChange={(e) => setFormData({ ...formData, dossier: e.target.value })}
                >
                  <option value="">Sélectionner un dossier</option>
                  {dossiers.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.ref} — {d.client}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: '0.25rem' }}>
                  Type *
                </label>
                <Select
                  value={formData.type || 'Lettre'}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value as any })}
                >
                  {['Lettre', 'Email', 'Fax', 'Courrier recommandé', 'Mise en demeure', 'Assignation', 'Mémoire', 'Autre'].map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </Select>
              </div>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: '0.25rem' }}>
                Titre *
              </label>
              <Input
                type="text"
                value={formData.titre || ''}
                onChange={(e) => setFormData({ ...formData, titre: e.target.value })}
                placeholder="Objet du courrier"
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: '0.25rem' }}>
                  Destinataire
                </label>
                <Input
                  type="text"
                  value={formData.destinataire || ''}
                  onChange={(e) => setFormData({ ...formData, destinataire: e.target.value })}
                  placeholder="Nom du destinataire"
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: '0.25rem' }}>Date</label>
                <Input
                  type="date"
                  value={formData.date || ''}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: '0.25rem' }}>Statut</label>
                <Select
                  value={formData.statut || 'Brouillon'}
                  onChange={(e) => setFormData({ ...formData, statut: e.target.value as any })}
                >
                  {['Brouillon', 'Signé', 'Envoyé'].map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </Select>
              </div>
            </div>

            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <label style={{ fontSize: 13, fontWeight: 500 }}>Contenu</label>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {formData.type && COURRIER_TEMPLATES[formData.type] && (
                    <Button
                      onClick={() => handleApplyTemplate(formData.type!)}
                      size="sm"
                      variant="secondary"
                    >
                      Template {formData.type}
                    </Button>
                  )}
                </div>
              </div>
              <textarea
                value={formData.contenu || ''}
                onChange={(e) => setFormData({ ...formData, contenu: e.target.value })}
                placeholder="Contenu du courrier"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  borderRadius: 'var(--border-radius-md)',
                  border: '1px solid var(--color-border-secondary)',
                  fontSize: 13,
                  minHeight: 300,
                  fontFamily: 'monospace',
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
              <Button onClick={handleSave} variant="primary">
                {isCreating ? 'Créer' : 'Enregistrer'}
              </Button>
              <Button onClick={handleCancel} variant="secondary">
                Annuler
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  // Filter courriers by dossier view
  const couriersByDossier: Record<string, Courrier[]> = {};
  data.courriers.forEach((c) => {
    if (!couriersByDossier[c.dossier]) {
      couriersByDossier[c.dossier] = [];
    }
    couriersByDossier[c.dossier].push(c);
  });

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h1 style={{ margin: 0, fontSize: 28 }}>Courriers</h1>
        <Button onClick={handleCreate}>+ Créer un courrier</Button>
      </div>

      {data.courriers.length === 0 ? (
        <Card style={{ textAlign: 'center', padding: '2rem' }}>
          <p style={{ color: 'var(--color-text-secondary)' }}>Aucun courrier pour le moment</p>
          <Button onClick={handleCreate} style={{ marginTop: '1rem' }}>
            Créer le premier courrier
          </Button>
        </Card>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
          {dossiers.map((dossier) => {
            const courriers = couriersByDossier[dossier.id] || [];
            if (courriers.length === 0) return null;

            return (
              <div key={dossier.id}>
                <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: '1rem', color: 'var(--color-text-primary)' }}>
                  {dossier.ref} — {dossier.client}
                </h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {courriers.map((courrier) => (
                    <Card key={courrier.id}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                        <div>
                          <p style={{ margin: '0 0 0.25rem', fontSize: 14, fontWeight: 600 }}>
                            {courrier.titre}
                          </p>
                          <p style={{ margin: '0 0 0.5rem', fontSize: 12, color: 'var(--color-text-secondary)' }}>
                            {courrier.type} • {courrier.statut}
                            {courrier.destinataire && ` • À: ${courrier.destinataire}`}
                          </p>
                          <p style={{ margin: 0, fontSize: 12, color: 'var(--color-text-tertiary)' }}>
                            {new Date(courrier.date).toLocaleDateString('fr-FR')}
                          </p>
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                          <Button onClick={() => handleEdit(courrier)} size="sm" variant="secondary">
                            Éditer
                          </Button>
                          <Button onClick={() => handleDelete(courrier.id)} size="sm" variant="secondary">
                            Supprimer
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
